import { LightMode } from "@/components/ui/color-mode";
import { AppColors } from "@/styles/colors";
import {
  Button,
  Card,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  GridItem,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Dispatch, SetStateAction } from "react";
import { calcGrossMarginCultivo } from "../schema/helper-functions";

interface CultivoConfirmData {
  granoNombre: string;
  cotizacionDolar: number;
  precioCereal: number;
  rindeEsperado: number;
  precioAgroquimicos: number;
  precioFertilizantes: number;
  precioSemillas: number;
  precioLabores: number;
  precioSeguros: number;
  precioAlquiler: number;
  gastoDeCosecha: number;
}

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  cultivosData: CultivoConfirmData[];
  onAccept: () => void;
  onCancel?: () => void;
  loading?: boolean;
}

const fmt = (n: number, decimals = 2) =>
  Number.isFinite(n)
    ? new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(n)
    : "-";

const SummaryItem = ({ label, value, index }: { label: string; value: string; index: number }) => {
  const match = value.match(/^(.+?)\s*\((.+)\)$/);
  return (
    <Flex
      alignItems="center"
      paddingY="10px"
      paddingX="12px"
      gap={2}
      bg={index % 2 === 0 ? AppColors.BACKGROUND_SECONDARY : AppColors.WHITE_SECONDARY}
    >
      <Text fontSize="14px" color={AppColors.BLACK_TEXT_SECONDARY} fontWeight={500}>
        {label}:
      </Text>
      {match ? (
        <Text fontSize="14px" color={AppColors.BLACK_TEXT_SECONDARY} fontWeight={400}>
          {match[1]} (<Text as="span" fontWeight={600}>{match[2]}</Text>)
        </Text>
      ) : (
        <Text fontSize="14px" color={AppColors.BLACK_TEXT_SECONDARY} fontWeight={400}>
          {value}
        </Text>
      )}
    </Flex>
  );
};

export const ConfirmGrossMarginModal = ({
  open,
  setOpen,
  cultivosData,
  onAccept,
  onCancel,
  loading,
}: Props) => {
  const handleCancel = () => {
    setOpen(false);
    onCancel?.();
  };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => { if (!e.open && !loading) handleCancel(); }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content maxW="680px" maxH="80vh">
              <Dialog.Header paddingY="16px" paddingX="24px">
                <Dialog.Title fontWeight={600} fontSize="18px">
                  Confirmar datos
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="8px" overflow="hidden" display="flex" flexDirection="column">
                <Text marginBottom="16px" fontSize="14px" color="gray.600">
                  Verificá el resumen antes de completar el alta.
                </Text>
                <Flex flexDir="column" gap="16px" overflowY="auto">
                  {cultivosData.map((c, i) => {
                    const calc = calcGrossMarginCultivo({
                      precioCereal: c.precioCereal,
                      cotizacionDolar: c.cotizacionDolar,
                      rindeEsperado: c.rindeEsperado,
                      precioAgroquimicos: c.precioAgroquimicos,
                      precioFertilizantes: c.precioFertilizantes,
                      precioSemillas: c.precioSemillas,
                      precioLabores: c.precioLabores,
                      precioSeguros: c.precioSeguros,
                      precioAlquiler: c.precioAlquiler,
                      gastoDeCosecha: c.gastoDeCosecha,
                    });

                    const toQqStr = (usd: number) =>
                      calc.pricePerQq > 0
                        ? `${fmt(usd / calc.pricePerQq)} qq/ha`
                        : "-";

                    const leftItems = [
                      { label: "Rinde esperado", value: `${fmt(c.rindeEsperado, 0)} qq` },
                      { label: "Agroquímicos", value: `U$S ${fmt(c.precioAgroquimicos)} (${toQqStr(c.precioAgroquimicos)})` },
                      { label: "Fertilizantes", value: `U$S ${fmt(c.precioFertilizantes)} (${toQqStr(c.precioFertilizantes)})` },
                      { label: "Semillas", value: `U$S ${fmt(c.precioSemillas)} (${toQqStr(c.precioSemillas)})` },
                      { label: "Labores", value: `U$S ${fmt(c.precioLabores)} (${toQqStr(c.precioLabores)})` },
                      { label: "Seguro", value: `U$S ${fmt(c.precioSeguros)} (${toQqStr(c.precioSeguros)})` },
                    ];

                    const rightItems = [
                      { label: "Alquiler", value: `${fmt(c.precioAlquiler)} qq/ha` },
                      { label: "Gasto de cosecha", value: `${fmt(c.gastoDeCosecha)} %` },
                      { label: "Inversión total", value: `U$S ${fmt(calc.inversionTotal)}` },
                      { label: "Rentabilidad", value: `${fmt(calc.rentabilidad)} %` },
                      { label: "Ganancia", value: `${fmt(calc.ganancia)} qq` },
                    ];

                    return (
                      <Card.Root key={i}>
                        <Card.Body borderWidth="1px" borderRadius="8px" padding="16px">
                          <Text
                            fontWeight={700}
                            fontSize="12px"
                            textTransform="uppercase"
                            marginBottom="12px"
                            color="gray.700"
                          >
                            {c.granoNombre}
                          </Text>
                          <Grid templateColumns="repeat(2, 1fr)" gap={0}>
                            <GridItem>
                              <Flex flexDir="column" gap="0">
                                {leftItems.map((item, idx) => (
                                  <SummaryItem key={idx} index={idx} label={item.label} value={item.value} />
                                ))}
                              </Flex>
                            </GridItem>
                            <GridItem>
                              <Flex flexDir="column" gap="0">
                                {rightItems.map((item, idx) => (
                                  <SummaryItem key={idx} index={idx} label={item.label} value={item.value} />
                                ))}
                              </Flex>
                            </GridItem>
                          </Grid>
                        </Card.Body>
                      </Card.Root>
                    );
                  })}
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px" justifyContent="flex-end" gap="12px">
                <Button
                  variant="subtle"
                  bgColor={AppColors.GRAY_BUTTON_BG}
                  paddingX="16px"
                  fontWeight={600}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={onAccept}
                  loading={loading}
                  disabled={loading}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export type { CultivoConfirmData };
